import { useState } from 'react';
import { Button, Modal, Form, Input, message, Card, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { axiosPost } from '../services/http.services'; // Assuming axios is set up
import { API_CHANGE_PASSWORD } from '../api/api';

const { Title, Paragraph } = Typography;

const parseAirports = (value) => {
	if (!value) {
		return [];
	}

	if (Array.isArray(value)) {
		return value;
	}

	try {
		return JSON.parse(value);
	} catch (error) {
		return [];
	}
};

const AccountSettings = () => {
	// Assuming user info is stored in the Redux store under state.auth
	const { userData: user } = useSelector((state) => state.auth);
	const userAirports = parseAirports(user?.airports);

	const [isModalVisible, setIsModalVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const [form] = Form.useForm();

	// Function to open the change password modal
	const handleChangePasswordModalOpen = () => {
		setIsModalVisible(true);
	};

	// Function to handle password change form submission
	const handleChangePassword = async (values) => {
		setLoading(true);
		try {
			const { currentPassword, newPassword } = values;
			// Send request to API to change password
			await axiosPost(API_CHANGE_PASSWORD, {
				currentPassword,
				newPassword,
				userid: user.userid,
			});

			message.success('Password updated successfully!');
			setIsModalVisible(false);
			form.resetFields();  // Reset form fields
		} catch (error) {
			console.log(error);
			message.error('Error updating password');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div style={{ maxWidth: 600, margin: 'auto', padding: '20px' }}>
			{/* Card for User Details */}
			<Card
				style={{
					boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
					borderRadius: '10px',
					marginBottom: '20px',
					padding: '20px',
				}}
				title={<Title level={3}>User Details</Title>}
			>
				<Typography>
					<Paragraph strong>Name: {user?.name}</Paragraph>
					<Paragraph strong>Username: {user?.username}</Paragraph>
					<Paragraph strong>Role: {user?.role}</Paragraph>
					<Paragraph strong>Airports: {userAirports.length ? userAirports.join(', ') : "All"}</Paragraph>
					<Paragraph strong>Created: {user?.created_at}</Paragraph>
				</Typography>

				{/* Change Password Button */}
				<Button
					type="primary"
					onClick={handleChangePasswordModalOpen}
					style={{
						width: '100%',
						marginTop: '20px',
						backgroundColor: '#1890ff',
						borderColor: '#1890ff',
						borderRadius: '5px',
					}}
				>
					Change Password
				</Button>
			</Card>

			{/* Change Password Modal */}
			<Modal
				title="Change Password"
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)} // Close modal
				footer={null}
				confirmLoading={loading}
				width={400}
				style={{ borderRadius: '10px' }}
			>
				<Form
					form={form}
					onFinish={handleChangePassword}  // Submit handler
					layout="vertical"
				>
					<Form.Item
						label="Current Password"
						name="currentPassword"
						rules={[{ required: true, message: 'Please input your current password!' }]}
					>
						<Input.Password />
					</Form.Item>

					<Form.Item
						label="New Password"
						name="newPassword"
						rules={[{ required: true, message: 'Please input your new password!' }, { min: 8, message: 'Password must be at least 8 characters!' }]}
					>
						<Input.Password />
					</Form.Item>

					{/* Confirm New Password Field */}
					<Form.Item
						label="Confirm New Password"
						name="confirmPassword"
						dependencies={['newPassword']}
						rules={[
							{ required: true, message: 'Please confirm your new password!' },
							({ getFieldValue }) => ({
								validator(_, value) {
									if (!value || getFieldValue('newPassword') === value) {
										return Promise.resolve();
									}
									return Promise.reject(new Error('The two passwords do not match!'));
								},
							}),
						]}
					>
						<Input.Password />
					</Form.Item>

					<Form.Item>
						<Button type="primary" htmlType="submit" style={{ width: '100%' }}>
							Change Password
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default AccountSettings;
